import { Link } from "react-router-dom";
import SectionHeading from "@/components/base/SectionHeading";

export interface BodyRegion {
  label: string;
  icon: string;
  text: string;
  to?: string;
}

interface BodyMapProps {
  eyebrow: string;
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  regions: BodyRegion[];
}

/**
 * Elegant "from head to toe" visual map of the areas the practice treats.
 * Regions link to the relevant service where one exists.
 */
export default function BodyMap({ eyebrow, title, description, image, imageAlt, regions }: BodyMapProps) {
  return (
    <section className="bg-background-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading eyebrow={eyebrow} title={title} description={description} />

        <div className="mt-12 grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="reveal relative lg:col-span-5">
            <div
              className="absolute -inset-x-4 bottom-10 top-6 rounded-[2.5rem] bg-primary-100/70"
              aria-hidden="true"
            ></div>
            <div className="relative overflow-hidden rounded-[2rem] border border-background-200 bg-background-100">
              <div className="h-[440px] w-full md:h-[600px]">
                <img
                  src={image}
                  alt={imageAlt}
                  title={imageAlt}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {regions.map((region) => {
                const content = (
                  <>
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-md bg-accent-100 text-accent-700">
                        <i className={`${region.icon} text-lg`} aria-hidden="true"></i>
                      </span>
                      <h3 className="font-heading text-base font-semibold text-foreground-950">{region.label}</h3>
                      {region.to ? (
                        <i
                          className="ri-arrow-right-line ml-auto text-primary-600 transition-transform duration-300 group-hover:translate-x-1"
                          aria-hidden="true"
                        ></i>
                      ) : null}
                    </div>
                    <p className="mt-3 font-body text-sm leading-relaxed text-foreground-600">{region.text}</p>
                  </>
                );

                return (
                  <li key={region.label} className="reveal">
                    {region.to ? (
                      <Link
                        to={region.to}
                        className="group flex h-full cursor-pointer flex-col rounded-xl border border-background-200 bg-background-100/60 p-5 transition-colors hover:border-primary-300 hover:bg-background-100"
                      >
                        {content}
                      </Link>
                    ) : (
                      <div className="group flex h-full flex-col rounded-xl border border-background-200 bg-background-100/60 p-5">
                        {content}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}